import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { client } from '../../client';
import { AppWrap, MotionWrap } from '../../wrapper';

const ease = [0.22, 1, 0.36, 1];

const SKILL_GROUPS = [
  {
    title: 'Frontend',
    items: ['React', 'Next.js', 'TypeScript', 'HTML', 'CSS', 'Tailwind CSS', 'Material UI'],
  },
  {
    title: 'Backend & APIs',
    items: ['Node.js', 'NestJS', 'Express.js', 'REST', 'GraphQL', 'AWS Lambda', 'AWS AppSync'],
  },
  {
    title: 'Data',
    items: ['PostgreSQL', 'MongoDB', 'MySQL', 'SQLAlchemy'],
  },
  {
    title: 'Testing & Collaboration',
    items: ['Jest', 'React Testing Library', 'Cypress', 'Selenium', 'Figma', 'Asana', 'Jira'],
  },
];

function SkillCard({ title, items }) {
  return (
    <div className="rounded-3xl border border-black/10 bg-white/70 p-6 shadow-[0_18px_45px_rgba(11,15,26,0.06)] sm:p-7">
      <h3 className="font-sans text-lg font-semibold tracking-[-0.01em] text-black/90">
        {title}
      </h3>

      <div className="mt-4 flex flex-wrap gap-2">
        {items.map((i) => (
          <span
            key={i}
            className="rounded-full border border-black/10 bg-transparent px-3 py-1 text-xs text-black/65"
          >
            {i}
          </span>
        ))}
      </div>
    </div>
  );
}

const Skills = () => {
  const [experiences, setExperiences] = useState([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    client.fetch('*[_type == "experiences"]').then(setExperiences);
  }, []);

  const sortedExperiences = useMemo(() => {
    const parseYear = (str = '') => {
      const [start, end] = str.split('-').map((s) => s.trim().toLowerCase());
      return {
        start: parseInt(start, 10) || 0,
        end: end === 'present' ? 9999 : parseInt(end || start, 10) || 0,
      };
    };

    return [...experiences].sort((a, b) => {
      const ay = parseYear(a.year);
      const by = parseYear(b.year);
      if (ay.end !== by.end) return by.end - ay.end;
      return by.start - ay.start;
    });
  }, [experiences]);

  const experiencesToShow = showAll ? sortedExperiences : sortedExperiences.slice(0, 3);

  return (
    <section className="mx-auto w-full max-w-5xl px-5 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-20">
      <div className="max-w-[64ch]">
        <p className="mb-3 text-[0.85rem] uppercase tracking-[0.12em] text-black/60">Toolbox</p>
        <h2 className="text-3xl font-semibold tracking-[-0.02em] text-black/90 sm:text-4xl">
          Skills & Experience
        </h2>
      </div>

      <div className="mt-10 grid gap-10">
        {/* Skills (hardcoded, editorial cards) */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease }}
          className="grid gap-4 md:grid-cols-2"
        >
          {SKILL_GROUPS.map((g) => (
            <SkillCard key={g.title} title={g.title} items={g.items} />
          ))}
        </motion.div>

        {/* Experience timeline (your existing code) */}
        <div className="grid gap-7">
          {experiencesToShow.map((exp) => (
            <div key={exp._id} className="grid gap-4 sm:grid-cols-[120px,1fr] sm:gap-6">
              <div className="text-xs uppercase tracking-[0.12em] text-black/60">{exp.year}</div>

              <div className="grid gap-4">
                {exp.works.map((work) => (
                  <motion.div
                    key={work._key || work.id || `${work.name}-${work.company}`}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.6, ease }}
                    className="border-t border-black/10 pt-4 first:border-t-0 first:pt-0"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <h4 className="text-base font-semibold text-black/90">{work.name}</h4>
                      <p className="text-sm text-black/60">{work.company}</p>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-black/75">{work.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}

          {sortedExperiences.length > 3 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll}
              className="w-fit border-b border-black/30 pb-1 text-sm text-black/80 transition hover:-translate-y-[1px] hover:border-black/60"
            >
              {showAll ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default AppWrap(MotionWrap(Skills, 'app__skills'), 'experience', 'app__whitebg');